import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Send, ShieldCheck, BadgeCheck, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPCOES = ["Formação Inicial", "Renovação", "Não tenho a certeza"];

const initial = { nome: "", telefone: "", email: "", formacao: "", consent: false };

export default function LeadForm() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, formacao: e.detail }));
    window.addEventListener("select-formacao", handler);
    return () => window.removeEventListener("select-formacao", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Precisas de aceitar a Política de Privacidade.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(initial);
      toast.success("Pedido recebido! A nossa equipa vai entrar em contacto contigo.");
    } catch {
      toast.error("Algo correu mal. Tenta novamente dentro de momentos.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-black/10 bg-[#ffffff] px-4 py-3.5 text-sm text-[#171717] placeholder-[#737373]/60 outline-none transition-colors duration-300 focus:border-[#34d186]";

  return (
    <section id="inscricao" data-testid="lead-form-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono-x text-xs uppercase tracking-[0.35em] text-[#34d186]">Quero receber informações</p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#171717] sm:text-3xl lg:text-4xl" data-testid="lead-form-title">
            Dá o primeiro passo para seres motorista Bolt.
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-[#737373] sm:text-lg">
            Preenche o formulário e a nossa equipa entrará em contacto contigo para esclarecer as tuas dúvidas e
            informar-te sobre as próximas turmas. Preencher o formulário não implica qualquer compromisso de inscrição.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: ShieldCheck, text: "Reconhecido pelo IMT" },
              { icon: BadgeCheck, text: "Grupo IFT · Parceiro de motoristas Bolt" },
              { icon: Zap, text: "Resposta rápida da nossa equipa" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-3 text-sm text-[#737373]">
                <b.icon size={17} className="text-[#34d186]" />
                {b.text}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="rounded-2xl border-2 border-[#34d186]/40 bg-[#fafafa] p-6 shadow-[0_0_40px_rgba(52,209,134,0.15)] sm:p-8"
          >
            <div className="space-y-5">
              <div>
                <label htmlFor="lead-nome" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#737373]">Nome</label>
                <input id="lead-nome" data-testid="lead-form-nome" required value={form.nome} onChange={set("nome")} placeholder="O teu nome completo" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-telefone" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#737373]">Telefone</label>
                <input id="lead-telefone" data-testid="lead-form-telefone" required type="tel" value={form.telefone} onChange={set("telefone")} placeholder="+351 ..." className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-email" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#737373]">Email</label>
                <input id="lead-email" data-testid="lead-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="oteuemail@exemplo.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-formacao" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#737373]">Qual a formação que procuras?</label>
                <select id="lead-formacao" data-testid="lead-form-formacao" required value={form.formacao} onChange={set("formacao")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Seleciona uma opção</option>
                  {OPCOES.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#737373]">
                <input
                  type="checkbox"
                  data-testid="lead-form-consent"
                  checked={form.consent}
                  onChange={set("consent")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#34d186]"
                />
                Concordo com a utilização dos meus dados para ser contactado com informações sobre o curso, de acordo com a Política de Privacidade.
              </label>
              <button
                type="submit"
                disabled={sending}
                data-testid="lead-form-submit"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#34d186] px-6 py-4 font-semibold text-[#ffffff] transition-all duration-300 hover:bg-[#00c46a] hover:shadow-[0_0_36px_rgba(52,209,134,0.5)] disabled:opacity-60"
              >
                {sending ? "A enviar..." : done ? "Quero ser contactado" : "Quero ser contactado"}
                <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
